import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  type = 'website',
  bookTitle,
  bookAuthor,
  highlightText
}) => {
  const defaultTitle = "스냅로그: 책 속 한 문장 기록, 스냅로그";
  const defaultDescription = "책에서 마음에 남는 문장을 기록하세요. 스냅로그는 나의 독서기록을 남기고, 공유하고, 되돌아볼 수 있는 독서기록 서비스입니다.";
  const defaultKeywords = "독서기록, 스냅로그, 독서기록앱, 책 기록, 문장 저장, 독서 메모, 책 리뷰, 독서 커뮤니티";
  const defaultImage = "/op-image.png";
  const baseUrl = "https://snaplog.blog";

  // 동적 제목 생성 (한국어 최적화)
  const getTitle = () => {
    if (bookTitle && highlightText) {
      return `${bookTitle} - ${highlightText.substring(0, 30)}... | 스냅로그`;
    } else if (bookTitle) {
      return `${bookTitle} | 스냅로그`;
    }
    return title || defaultTitle;
  };

  // 동적 설명 생성 (한국어 최적화)
  const getDescription = () => {
    if (highlightText) {
      const cleanText = highlightText.replace(/\s+/g, ' ').trim();
      return `${cleanText.substring(0, 100)}... - ${defaultDescription}`;
    } else if (bookTitle) {
      return `${bookTitle}${bookAuthor ? ` (${bookAuthor})` : ''} 독서기록을 확인하세요. ${defaultDescription}`;
    }
    return description || defaultDescription;
  };

  // 동적 키워드 생성 (한국어 최적화)
  const getKeywords = () => {
    let dynamicKeywords = defaultKeywords;
    if (bookTitle) {
      dynamicKeywords += `, ${bookTitle}`;
    }
    if (bookAuthor) {
      dynamicKeywords += `, ${bookAuthor}`;
    }
    if (highlightText) {
      const cleanText = highlightText.replace(/\s+/g, ' ').trim();
      dynamicKeywords += `, ${cleanText.substring(0, 50)}`;
    }
    return keywords || dynamicKeywords;
  };

  const currentUrl = url ? `${baseUrl}${url}` : baseUrl;
  
  // 이미지 URL 처리 - 외부 URL인지 내부 경로인지 확인
  const getImageUrl = (imagePath) => {
    if (!imagePath) return `${baseUrl}${defaultImage}`;
    
    // 외부 URL인 경우 (http:// 또는 https://로 시작)
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // 내부 경로인 경우 baseUrl과 결합
    return `${baseUrl}${imagePath}`;
  };
  
  const currentImage = getImageUrl(image);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{getTitle()}</title>
      <meta name="description" content={getDescription()} />
      <meta name="keywords" content={getKeywords()} />
      <meta name="author" content="스냅로그" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="language" content="ko" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Open Graph / Facebook - 한국어 최적화 */}
      <meta property="og:title" content={getTitle()} />
      <meta property="og:description" content={getDescription()} />
      <meta property="og:image" content={currentImage} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="스냅로그" />
      <meta property="og:locale" content="ko_KR" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={bookTitle ? `${bookTitle} - 스냅로그 독서기록` : "스냅로그 - 독서기록 서비스"} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:secure_url" content={currentImage} />

      {/* Twitter - 한국어 최적화 */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={getTitle()} />
      <meta name="twitter:description" content={getDescription()} />
      <meta name="twitter:image" content={currentImage} />
      <meta name="twitter:image:alt" content={bookTitle ? `${bookTitle} - 스냅로그 독서기록` : "스냅로그 - 독서기록 서비스"} />
      <meta name="twitter:site" content="@snaplog" />
      <meta name="twitter:creator" content="@snaplog" />

      {/* Additional Meta Tags for Books */}
      {bookTitle && (
        <meta property="og:article:section" content="독서기록" />
      )}
      {bookTitle && (
        <meta property="og:article:tag" content={bookTitle} />
      )}
      
      {bookAuthor && (
        <meta property="og:article:author" content={bookAuthor} />
      )}

      {/* 네이버 블로그 공유 최적화 */}
      <meta property="naver:title" content={getTitle()} />
      <meta property="naver:description" content={getDescription()} />
      <meta property="naver:image" content={currentImage} />

      {/* 카카오톡 공유 최적화 */}
      <meta property="kakao:title" content={getTitle()} />
      <meta property="kakao:description" content={getDescription()} />
      <meta property="kakao:image" content={currentImage} />

      {/* 추가 SEO 메타태그 */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="스냅로그" />
      
      {/* 구조화된 데이터 힌트 */}
      {bookTitle && (
        <meta name="application-name" content="스냅로그 독서기록" />
      )}
    </Helmet>
  );
};

export default SEO; 